/* eslint-disable react/display-name */
import { Box, Flex, Link, Skeleton } from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  useGetLimitedPartnerCommitmentsForLenderQuery,
  LimitedPartnerCommitmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { ActionsMenu } from './LimitedPartnersDashboard';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';

export const LimitedPartnerCommitmentsTable = ({
  limitedPartnerCommitments,
}: {
  limitedPartnerCommitments: LimitedPartnerCommitmentGQLType[];
}) => {
  const columns = useMemo(() => {
    const tableColumns: ColumnDef<LimitedPartnerCommitmentGQLType, any>[] = [
      {
        accessorKey: 'limitedPartnerAlias',
        header: 'Name',
      },
      {
        accessorKey: 'limitedPartnerName',
        header: 'LP',
        cell: ({ row }) => (
          <>
            {row.original.limitedPartnerName && row.original.limitedPartnerId && (
              <Link
                color="blue.400"
                to={`/limited-partners/details/${row.original.limitedPartnerId}`}
                as={RouterLink}
                _hover={{
                  textDecoration: 'underline',
                }}
                style={{ wordBreak: 'break-word' }}
              >
                {row.original.limitedPartnerName}
              </Link>
            )}
          </>
        ),
      },
      {
        accessorKey: 'creditFacilityName',
        header: 'Facility',
      },
      {
        accessorKey: 'borrowerName',
        header: 'Borrower',
      },
      {
        accessorKey: 'committedCapital',
        header: 'Committed Capital',
        cell: (cell) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: Number(cell.row.original.committedCapital),
          }),
      },
      {
        accessorKey: 'uncalledCapital',
        header: 'Uncalled Capital',
        cell: (cell) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: Number(cell.row.original.uncalledCapital),
          }),
      },
      {
        accessorKey: 'reportingDate',
        header: 'Reporting Date',
        cell: (cell) =>
          commonDate.fullDateStrToHumanDateString(
            cell.row.original.reportingDate,
          ),
      },
    ];

    return tableColumns;
  }, []);

  const table = useReactTable({
    data: limitedPartnerCommitments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    getRowId: (row) => row.finleyReceivableId,
  });

  return <TableBETA table={table} FlexContainerProps={{ maxHeight: '65vh' }} />;
};

export const LimitedPartnerCommitments = () => {
  const { user } = useUserAuthOrThrow();

  const {
    data: limitedPartnerCommitmentsData,
    error: limitedPartnerCommitmentsError,
  } = useGetLimitedPartnerCommitmentsForLenderQuery({
    variables: {
      lenderOrganizationId: user.organizationId,
    },
    fetchPolicy: 'cache-and-network',
  });
  if (limitedPartnerCommitmentsError) {
    throw limitedPartnerCommitmentsError;
  }
  return (
    <Box padding={dashboardRoutePadding}>
      <Flex justifyContent="space-between" mb="1.5rem">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Limited Partners', path: `/limited-partners` },
          ]}
        />
        <ActionsMenu />
      </Flex>
      <Typography variant="page-title" trim>
        LP Commitments
      </Typography>
      {limitedPartnerCommitmentsData ? (
        <Box mt="2.5rem">
          <LimitedPartnerCommitmentsTable
            limitedPartnerCommitments={
              limitedPartnerCommitmentsData.limitedPartnerCommitments
            }
          />
        </Box>
      ) : (
        <Skeleton height="80vh" mt="2.5rem" />
      )}
    </Box>
  );
};
